import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { Button } from '@hqo/ui-atoms';

import { OfferBox, OfferButtonIcon, OfferText } from './styles';

interface AddOnsOfferProps {
  /* The id to hand the step. Decided by the caller, like AddOnsPrompt's -- see its comment. */
  bookingId: string;
  /*
   * Navigation is injected, and that is the GUARD rather than tidiness: the add-on step is a nested
   * route of PaymentContent, which the routed booking-details page does not mount. No navigator, no
   * box -- so this cannot offer a door that opens onto nothing.
   */
  onBrowseAddOns: (bookingId: string) => void;
}

/**
 * The offer, on a booking that ordered nothing and still could.
 *
 * RES-1367. Deliberately NOT `AddOnsPrompt` (components/payment/receipt/components), which is the
 * same offer made at a different moment and in a different shape: that one is a full-width card on
 * the screen a tenant lands on having just booked, built out of the check-in card and carrying its
 * sizing quirks. This is a small box at the foot of a column they are browsing, where the booking
 * is the subject and the offer is an aside.
 *
 * The copy is shared with it on purpose -- one offer, one set of words, three message ids between
 * them -- because the thing being offered is the same thing.
 */
export const AddOnsOffer = ({ bookingId, onBrowseAddOns }: AddOnsOfferProps): JSX.Element => {
  const intl = useIntl();

  const onClick = useCallback(() => onBrowseAddOns(bookingId), [onBrowseAddOns, bookingId]);

  return (
    <OfferBox data-testid="add-ons-offer">
      <OfferText>{intl.formatMessage({ id: 'add_on_prompt_body' })}</OfferText>
      {/*
        ui-atoms' `secondary` is this pill: `border border-primary bg-background text-primary`, and it
        reads `--primary` -- the variable this app brands -- rather than the styled-components theme's
        parallel copy. The variant also brings the focus ring the other buttons on this card have.

        `has-[>svg]:px-6` is not a duplicate of `px-6`. The size variant ships `has-[>svg]:px-3`, at
        higher specificity than a bare `px-*`; tailwind-merge does not treat the two as the same
        utility, so a lone `px-6` survives into the class list and then loses at paint -- and this
        button always has an svg. (SelectTrigger sets the same trap with `data-[size=default]:h-9`.)
      */}
      <Button
        type="button"
        variant="secondary"
        className="rounded-full px-6 has-[>svg]:px-6"
        onClick={onClick}
        data-testid="add-ons-offer-cta"
      >
        <OfferButtonIcon icon={faPlus} />
        {intl.formatMessage({ id: 'add_on_prompt_cta' })}
      </Button>
    </OfferBox>
  );
};
